import { useEffect, useState } from 'react';

const isVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * The current time (epoch ms), refreshed every `intervalMs` so relative
 * times ("4 min ago", "18 h 20 min since pour start") stay live.
 *
 * It ticks only while the page is visible: a phone in a pocket does no work.
 * When the page becomes visible again it ticks at once, so the text is
 * right the moment the user looks.
 */
export const useNow = (intervalMs: number = 60000): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      stop();
      timer = setInterval(() => setNow(Date.now()), intervalMs);
    };
    const onVisibilityChange = () => {
      if (isVisible()) {
        setNow(Date.now());
        start();
      } else {
        stop();
      }
    };

    if (isVisible()) start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs]);

  return now;
};

export default useNow;
